import React, { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import OrdersPageActions from 'components/orders/shared/orders-page-actions.component';
import { getOrdersForTechnicianTemplate } from 'apollo/templates/order';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { getOrderListRowTemplate } from 'apollo/templates/order/query.graphql';
import { useLiveOrderList } from 'hooks/useLiveOrderList';
import { Order, OrderFilter, OrderTableType } from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import OrderTableContainer from 'components/orders/order-table-container.container';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { OrderBy, OrderDirection } from 'models/search-result';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ListViewProfile } from 'models/organization';
import {
    getActiveOrderListColumnKeys,
    SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE,
} from 'components/orders/lab-order-table/column-registry';
import { buildIncludeVariables } from 'components/orders/lab-order-table/build-include-variables';
import { resolveTechnicianFilter } from 'components/shared/order-filters/technician-filter.utils';

type DentistOrdersProps = {
    batchActionSelected: boolean;
    onHandleCheckedOrders: (order: Order, checked: boolean, orderIndex: number) => void;
    onSortChange?: () => void;
    onFilterChange?: () => void;
    refetchOrder: boolean;
    labOrderListProfile?: ListViewProfile;
    profileLoading?: boolean;
    isAtLimit?: boolean;
    checkedOrderIds?: Set<string>;
};

const LabOrders: FC<DentistOrdersProps> = ({
    batchActionSelected,
    onHandleCheckedOrders,
    onSortChange,
    onFilterChange,
    refetchOrder,
    labOrderListProfile,
    profileLoading,
    isAtLimit,
    checkedOrderIds,
}: DentistOrdersProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const liveList = useLiveOrderList('ordersForTechnician', getOrderListRowTemplate);
    const [manualSort, setManualSort] = useState<{ profileId: string; orderBy: OrderBy } | null>(null);
    const [orderFilters, setOrderFilters] = useState<OrderFilter>(
        JSON.parse(localStorage.getItem('order-list-filters') || '{}'),
    );
    const { showBoundary } = useDenthubErrorBoundary();
    const isExternalTechnician = permissions.isExternalTechnician(currentUser?.role);

    const activeProfile = labOrderListProfile ?? SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE;
    const activeColumnKeys = getActiveOrderListColumnKeys(activeProfile);
    const includeVariables = buildIncludeVariables(activeColumnKeys);
    const includeKey = JSON.stringify(includeVariables);
    const [fetchedIncludeKey, setFetchedIncludeKey] = useState<string>('');

    const profileDefaultOrderBy: OrderBy = useMemo(() => {
        if (!isExternalTechnician && activeProfile.defaultSortField) {
            return {
                orderBy: activeProfile.defaultSortField,
                orderDirection: activeProfile.defaultSortDirection === 'asc' ? OrderDirection.asc : OrderDirection.desc,
            };
        }
        return {
            orderBy: isExternalTechnician ? 'linkedExtTechs.dateAdded' : 'created.on',
            orderDirection: OrderDirection.desc,
        };
    }, [activeProfile.defaultSortField, activeProfile.defaultSortDirection, isExternalTechnician]);

    const orderBy: OrderBy =
        manualSort && manualSort.profileId === activeProfile._id ? manualSort.orderBy : profileDefaultOrderBy;

    const [getOrdersForTechnician, extOrdersResult] = useLazyQuery(getOrdersForTechnicianTemplate, {
        variables: { limit: 20, offset: 0 },
        context: liveList.context,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        pollInterval: 5 * 60 * 1000,
        notifyOnNetworkStatusChange: true,
        onCompleted: () => {
            setFetchedIncludeKey(includeKey);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { loading, fetchMore, networkStatus, refetch, data } = extOrdersResult;
    const orders = liveList.ordersFrom(data);
    const lastOffset = orders.length;
    const displayOrders = fetchedIncludeKey === includeKey ? orders : [];

    const buildVariables = (overrides: Record<string, unknown> = {}) => ({
        limit: 20,
        offset: 0,
        filters: resolveTechnicianFilter(orderFilters, currentUser._id),
        ...orderBy,
        ...overrides,
    });

    // Route everyone through getOrdersForTechnician. Column profiles are filtered
    // client-side from the full payload. The orderListItems resolver path
    // is retained but not used here due to a memory issue under nav alternation.
    const runQuery = (overrides: Record<string, unknown> = {}) => {
        getOrdersForTechnician({ variables: buildVariables(overrides) });
    };

    useEffect(() => {
        if (refetchOrder && refetch) {
            refetch(buildVariables());
        }
    }, [refetchOrder]);

    useEffect(() => {
        if (profileLoading) return;
        runQuery();
    }, [profileLoading, orderFilters, isExternalTechnician, includeKey, orderBy.orderBy, orderBy.orderDirection]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({ variables: buildVariables({ offset: lastOffset }) });
        } else {
            runQuery();
        }
    };

    const handleFilterChange = useCallback(
        (filters: OrderFilter) => {
            setOrderFilters(filters);
            onFilterChange?.();
        },
        [onFilterChange],
    );

    const setOrderOnField = (field: string) => {
        const isSameField = orderBy.orderBy === field;
        const direction = isSameField
            ? orderBy.orderDirection === OrderDirection.asc
                ? OrderDirection.desc
                : OrderDirection.asc
            : OrderDirection.desc;
        setManualSort({
            profileId: activeProfile._id,
            orderBy: { orderBy: field, orderDirection: direction },
        });
        onSortChange?.();
    };

    const handleUpdateOrderListForArchived = (orderId: string) => liveList.removeOrders([orderId]);

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:ORDERS')} | Denthub</title>
            </Helmet>
            <OrdersPageActions
                initFilters={orderFilters}
                onFilterChange={handleFilterChange}
                hideClinicFilter={isExternalTechnician}
                hideSupplierFilter={true}
                showTechnicianFilter={!isExternalTechnician}
            />
            {loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.poll ? (
                <DelayedLinearProgress />
            ) : (
                <Fragment>
                    {isExternalTechnician ? (
                        <OrderTableContainer
                            setOrderOnField={setOrderOnField}
                            tableType={OrderTableType.EXTERNAL_TECHNICIAN}
                            orderBy={orderBy}
                            orders={displayOrders}
                            hidePatientDetails={permissions.isRestrictedExternalUser(currentUser?.role)}
                            onFetchMore={handleFetchMore}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : (
                        <OrderTableContainer
                            tableType={OrderTableType.LAB}
                            orderBy={orderBy}
                            setOrderOnField={setOrderOnField}
                            orders={displayOrders}
                            onFetchMore={handleFetchMore}
                            handleUpdateOrderListForArchived={handleUpdateOrderListForArchived}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            labOrderListProfile={labOrderListProfile}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    )}
                </Fragment>
            )}
        </Fragment>
    );
};

export default LabOrders;
